import { useCallback, useEffect, useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import {
  money,
  rebates,
  type BulkResult,
  type RebateConfig,
  type RebateListItem,
  type RebateStatus,
  type RebateTotals,
} from '../../services/hostedEvents'
import { RebateDetailModal } from './RebateDetailModal'
import { downloadCsv, errorMessage, shortDate } from './helpers'
import {
  Badge,
  Empty,
  ErrorBanner,
  Modal,
  Notice,
  Pager,
  Spinner,
  Stat,
  StatusBadge,
  clickableRow,
  inputClass,
  labelClass,
  tableWrap,
  theadClass,
} from './ui'

const LIMIT = 50
const EXPORT_PAGE = 200
const EXPORT_MAX = 50000

const TABS: {
  status: RebateStatus | ''
  label: string
  count: (totals: RebateTotals) => number | null
}[] = [
  { status: 'pending', label: 'To review', count: (totals) => totals.pending_count },
  { status: 'approved', label: 'To pay', count: (totals) => totals.approved_count },
  { status: 'paid', label: 'Paid', count: (totals) => totals.paid_count },
  { status: 'rejected', label: 'Rejected', count: (totals) => totals.rejected_count },
  { status: 'void', label: 'Void', count: (totals) => totals.void_count },
  { status: '', label: 'All', count: () => null },
]

export function RebatesTable({
  marketId,
  config,
  onChanged,
}: {
  marketId?: string
  config: RebateConfig
  onChanged?: () => void
}) {
  const canReview = usePermission('event_rebates', 'review')
  const [status, setStatus] = useState<RebateStatus | ''>('pending')
  const [offset, setOffset] = useState(0)
  const [items, setItems] = useState<RebateListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totals, setTotals] = useState<RebateTotals | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [openId, setOpenId] = useState<string | null>(null)
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | null>(null)
  const [bulkReason, setBulkReason] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null)
  const [exporting, setExporting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await rebates.list({ status, market_id: marketId, limit: LIMIT, offset })
      if (result.items.length === 0 && offset > 0) {
        setOffset(Math.max(0, (Math.ceil(result.total / LIMIT) - 1) * LIMIT))
        return
      }
      setItems(result.items)
      setTotal(result.total)
      setTotals(result.totals)
      setSelected(new Set())
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setLoading(false)
    }
  }, [status, marketId, offset])

  useEffect(() => {
    load()
  }, [load])

  const changed = () => {
    load()
    onChanged?.()
  }

  const selectable = items.filter((item) => item.status === 'pending')
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.has(item.id))
  const toggle = (rebateId: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(rebateId)) next.delete(rebateId)
      else next.add(rebateId)
      return next
    })

  const runBulk = async () => {
    if (!bulkAction) return
    setBulkBusy(true)
    setError(null)
    try {
      const result = await rebates.bulk(bulkAction, [...selected], bulkReason.trim() || null)
      setBulkResult(result)
      setBulkAction(null)
      setBulkReason('')
      changed()
    } catch (caught) {
      setError(errorMessage(caught))
      setBulkAction(null)
    } finally {
      setBulkBusy(false)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    setError(null)
    try {
      const rows: RebateListItem[] = []
      let matching = 0
      while (rows.length < EXPORT_MAX) {
        const page = await rebates.list({
          status,
          market_id: marketId,
          limit: EXPORT_PAGE,
          offset: rows.length,
        })
        rows.push(...page.items)
        matching = page.total
        if (page.items.length < EXPORT_PAGE) break
      }
      const partial = matching > rows.length
      if (partial)
        setError(
          `This export holds the first ${rows.length} of ${matching} rebates. Narrow the filter to get the rest.`
        )
      downloadCsv(
        `rebates-${status || 'all'}${partial ? '-partial' : ''}-${new Date().toISOString().slice(0, 10)}.csv`,
        [
          [
            'rebate_id',
            'event',
            'event_date',
            'vendor',
            'handle',
            'status',
            'amount_usd',
            'computed_usd',
            'exception',
            'payout_method',
            'payout_reference',
            'paid_at',
            'decision_reason',
          ],
          ...rows.map((row) => [
            row.id,
            row.market_name,
            row.market_starts_at ? row.market_starts_at.slice(0, 10) : null,
            row.shop_name,
            row.shop_handle,
            row.status,
            (row.amount_cents / 100).toFixed(2),
            (row.computed_cents / 100).toFixed(2),
            row.is_exception ? 'yes' : 'no',
            row.payout_method,
            row.payout_reference,
            row.paid_at,
            row.decision_reason,
          ]),
        ]
      )
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setExporting(false)
    }
  }

  const names = new Map(items.map((item) => [item.id, item.shop_name || item.id.slice(0, 8)]))

  return (
    <div>
      {totals && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="To review" value={totals.pending_count} hint={money(totals.pending_cents)} />
          <Stat
            label="Approved, unpaid"
            value={totals.approved_count}
            hint={money(totals.approved_cents)}
          />
          <Stat
            label="Paid out"
            value={money(totals.paid_cents)}
            hint={`${totals.paid_count} rebate(s)`}
          />
          <Stat
            label="Committed"
            value={money(totals.committed_cents)}
            hint={
              totals.remaining_budget_cents === null
                ? 'Approved plus paid'
                : `${money(totals.remaining_budget_cents)} of budget left`
            }
          />
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Rebate status">
          {TABS.map((tab) => {
            const count = totals ? tab.count(totals) : null
            const active = status === tab.status
            return (
              <button
                key={tab.label}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setStatus(tab.status)
                  setOffset(0)
                }}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  active
                    ? 'bg-[#7f4f9a] text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2d2236]'
                }`}
              >
                {tab.label}
                {count !== null && <span className="ml-1.5 opacity-80">{count}</span>}
              </button>
            )
          })}
        </div>
        <CustomButton
          size="sm"
          variant="secondary"
          onClick={exportCsv}
          loading={exporting}
          disabled={total === 0}
        >
          Export CSV
        </CustomButton>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {bulkResult && (
        <div className="mb-3">
          <Notice tone={bulkResult.failed ? 'amber' : 'green'}>
            {bulkResult.succeeded} updated
            {bulkResult.failed > 0 && `, ${bulkResult.failed} refused`}.
            {bulkResult.failed > 0 && (
              <ul className="mt-1 list-inside list-disc">
                {bulkResult.results
                  .filter((result) => !result.ok)
                  .map((result) => (
                    <li key={result.rebate_id}>
                      {names.get(result.rebate_id) || result.rebate_id.slice(0, 8)}: {result.error}
                    </li>
                  ))}
              </ul>
            )}
            <button type="button" className="ml-2 underline" onClick={() => setBulkResult(null)}>
              Dismiss
            </button>
          </Notice>
        </div>
      )}

      {canReview && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#faf6ff] px-4 py-2 text-sm dark:bg-[#221728]">
          <span className="text-gray-700 dark:text-gray-200">{selected.size} selected</span>
          <CustomButton size="sm" variant="success" onClick={() => setBulkAction('approve')}>
            Approve
          </CustomButton>
          <CustomButton size="sm" variant="danger" onClick={() => setBulkAction('reject')}>
            Reject
          </CustomButton>
          <CustomButton size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </CustomButton>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty>
          {status === 'pending'
            ? 'Nothing to review. Open rebates from an event’s Vendors tab once its program is active.'
            : 'No rebates here.'}
        </Empty>
      ) : (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadClass}>
              <tr>
                {canReview && (
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all pending rebates on this page"
                      disabled={selectable.length === 0}
                      checked={allSelected}
                      onChange={() =>
                        setSelected(
                          allSelected ? new Set() : new Set(selectable.map((item) => item.id))
                        )
                      }
                    />
                  </th>
                )}
                <th className="px-4 py-3">Vendor</th>
                {!marketId && <th className="px-4 py-3">Event</th>}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Opened</th>
                <th className="px-4 py-3">Payout</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={clickableRow}
                  tabIndex={0}
                  onClick={() => setOpenId(item.id)}
                  onKeyDown={(event) => event.key === 'Enter' && setOpenId(item.id)}
                >
                  {canReview && (
                    <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                      {item.status === 'pending' && (
                        <input
                          type="checkbox"
                          aria-label={`Select rebate for ${item.shop_name || 'vendor'}`}
                          checked={selected.has(item.id)}
                          onChange={() => toggle(item.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {item.shop_name || 'Unknown'}
                    </div>
                    {item.shop_handle && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        @{item.shop_handle}
                      </div>
                    )}
                  </td>
                  {!marketId && (
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {item.market_name || 'Unknown'}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {shortDate(item.market_starts_at)}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={item.status} />
                      {item.is_exception && <Badge tone="purple">Exception</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                    {money(item.amount_cents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {shortDate(item.created_at)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {item.paid_at
                      ? `${config.payout_methods.find((method) => method.id === item.payout_method)?.label || item.payout_method} · ${shortDate(item.paid_at)}`
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager offset={offset} limit={LIMIT} total={total} onChange={setOffset} />

      {openId && (
        <RebateDetailModal
          rebateId={openId}
          config={config}
          showEventLink={!marketId}
          onClose={() => setOpenId(null)}
          onChanged={changed}
        />
      )}

      {bulkAction && (
        <Modal
          title={`${bulkAction === 'approve' ? 'Approve' : 'Reject'} ${selected.size} rebate(s)`}
          onClose={() => setBulkAction(null)}
        >
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {bulkAction === 'approve'
              ? 'Each rebate is approved at its current amount. Any that would go over the budget or recipient limit are refused and reported back.'
              : 'These vendors will not receive a rebate. You can reopen a rejection later.'}
          </p>
          <label className={`${labelClass} mt-4`} htmlFor="bulk-reason">
            Reason{bulkAction === 'approve' ? ' (optional)' : ''}
          </label>
          <textarea
            id="bulk-reason"
            className={inputClass}
            rows={3}
            maxLength={500}
            value={bulkReason}
            onChange={(event) => setBulkReason(event.target.value)}
          />
          {selected.size > config.bulk_limit && (
            <div className="mt-3">
              <Notice tone="amber">At most {config.bulk_limit} rebates per request.</Notice>
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <CustomButton
              variant="secondary"
              onClick={() => setBulkAction(null)}
              disabled={bulkBusy}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant={bulkAction === 'approve' ? 'success' : 'danger'}
              loading={bulkBusy}
              disabled={
                selected.size > config.bulk_limit || (bulkAction === 'reject' && !bulkReason.trim())
              }
              onClick={runBulk}
            >
              Confirm
            </CustomButton>
          </div>
        </Modal>
      )}
    </div>
  )
}
